/**
 * OAuth Callback Handler
 * Handles the redirect from Google OAuth and exchanges code for tokens
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GoogleOAuthService from '../../services/GoogleOAuthService';
import api from '../../services/api';
import { setSession } from '../../utils/authUtils';
import tokenManager from '../../auth/TokenManager';
import logger from '../../utils/logger';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  padding: '20px',
});

const AuthCard = styled(Paper)({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '48px 40px',
  textAlign: 'center',
  maxWidth: '420px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  animation: `${fadeIn} 0.5s ease-out`,
});

const LogoContainer = styled(Box)({
  marginBottom: '32px',
});

const StatusIconContainer = styled(Box)({
  marginBottom: '24px',
});

const Title = styled(Typography)({
  fontSize: '24px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '12px',
  fontFamily: 'Inter, sans-serif',
});

const StatusMessage = styled(Typography)({
  fontSize: '15px',
  color: '#64748B',
  fontFamily: 'Inter, sans-serif',
  animation: `${pulse} 2s ease-in-out infinite`,
});

const ErrorMessage = styled(Box)({
  backgroundColor: '#FEF2F2',
  border: '1px solid #FECACA',
  borderRadius: '8px',
  padding: '12px 16px',
  marginTop: '16px',
});

const StepIndicator = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '32px',
});

const StepDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed'
})(({ active, completed }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: completed ? '#10B981' : active ? '#0B91D6' : '#E2E8F0',
  transition: 'all 0.3s ease',
}));

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);

  const steps = [
    'Exchanging authorization code...',
    'Getting user information...',
    'Logging in...',
    'Success! Redirecting...'
  ];

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Clear any stale admin session data from previous admin access
        // This prevents clients from seeing admin-viewed profiles after login
        sessionStorage.removeItem('adminAccessData');
        sessionStorage.removeItem('adminViewingUserId');
        sessionStorage.removeItem('currentUserContext');
        sessionStorage.removeItem('visibeen_default_profile');
        sessionStorage.removeItem('googleAccessToken');
        sessionStorage.removeItem('adminView_googleAccessToken');
        localStorage.removeItem('latestAdminAccessTempKey');
        // Clear cached GMB profiles to ensure fresh data for the new user
        localStorage.removeItem('availableGMBProfiles');
        localStorage.removeItem('availableGMBProfilesOwner');

        // Clear any stale trial markers so previous user's trial doesn't leak
        try { sessionStorage.removeItem('trial_invite'); } catch (_) { }
        try { localStorage.removeItem('trial_invite'); } catch (_) { }

        // Get code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');

        // Check for errors from Google
        if (errorParam) {
          const errorDescription = searchParams.get('error_description') || 'Authentication failed';
          throw new Error(errorDescription);
        }

        if (!code) {
          throw new Error('Authorization code not found');
        }

        setStatus('Exchanging authorization code...');
        setCurrentStep(0);

        // Exchange code for tokens
        const tokens = await GoogleOAuthService.handleCallback(code, state);

        if (!tokens.access_token) {
          throw new Error('Access token not received');
        }

        setStatus('Getting user information...');
        setCurrentStep(1);

        // Get user info from Google
        const userInfo = await GoogleOAuthService.getUserInfo(tokens.access_token);

        setStatus('Logging in...');
        setCurrentStep(2);

        // Send to backend for user creation/login (normal flow — no trial handling here)
        const response = await api.post('/customer/auth/google-login', {
          email: userInfo.email,
          full_name: userInfo.name,
          googleAccessToken: tokens.access_token,
          authCode: null, // We already exchanged the code
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in
        });

        logger.log('Backend response:', response);

        // Backend returns: { error: null, code: 200, data: { user, hasGMBAccess, gmbAccounts }, message }
        // Check for successful response (error is null and we have user data)
        const success = response?.error === null || response?.code === 200;
        const userData = response?.data?.user;

        if (success && userData) {

          // Store tokens
          if (tokens.access_token) {
            tokenManager.set('google', {
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
              expires_in: tokens.expires_in,
              token_type: 'Bearer'
            });

            // Legacy storage
            try {
              localStorage.setItem('googleAccessToken', tokens.access_token);
              if (tokens.refresh_token) {
                localStorage.setItem('googleRefreshToken', tokens.refresh_token);
              }
            } catch (e) {
              logger.error('Failed to store tokens in localStorage:', e);
            }
          }

          // Set user session
          setSession(userData);
          // No trial handling during login — keep normal auth flow

          setStatus('Success! Redirecting...');
          setCurrentStep(3);

          // Check if user has GMB access
          const hasGMBAccess = response?.data?.hasGMBAccess || false;

          // Check if this is truly a new user (created during this login) - use backend's isNewUser flag
          // isNewUser from backend = true ONLY if user was created right now during this login
          const isNewUserFromBackend = response?.data?.isNewUser === true;

          // ✅ FIX: Only NEW users (created just now) should see pricing/onboarding
          // Existing users in DB should NEVER see pricing, regardless of onboarding_completed status
          // Legacy users may have onboarding_completed = null - they are still existing users
          const hasCompletedOnboarding = !isNewUserFromBackend; // If not new, they've "completed" onboarding (skip it)

          // Set isNewUser for welcome message - use the backend flag
          setIsNewUser(isNewUserFromBackend);

          logger.log('OAuth Login Check:');
          logger.log('- User has GMB access:', hasGMBAccess);
          logger.log('- onboarding_completed (from API):', userData.onboarding_completed);
          logger.log('- isNewUser (from backend):', isNewUserFromBackend);
          logger.log('- Has completed onboarding (calculated):', hasCompletedOnboarding);

          // Redirect logic
          setTimeout(() => {
            // If visitor came from a trial link, prefer pricing for non-admin users
            try {
              const cameFromTrialLocal = localStorage.getItem('came_from_trial');
              let cameFromTrial = cameFromTrialLocal;
              if (!cameFromTrial) {
                try {
                  const cookieMatch = document.cookie.split('; ').find(c => c.startsWith('came_from_trial='));
                  if (cookieMatch) cameFromTrial = cookieMatch.split('=')[1];
                } catch (_) { }
              }

              if (cameFromTrial && ![9].includes(userData.role_id)) {
                try {
                  // Persist as trial_invite so DashboardLayout shows the snackbar popup
                  const inv = { token: cameFromTrial, sig: null, locationId: null, source: null, ts: Date.now() };
                  const inviteStr = JSON.stringify(inv);
                  try { sessionStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                  try { localStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                  try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
                } catch (_) { }
                try { localStorage.removeItem('came_from_trial'); localStorage.removeItem('came_from_trial_ts'); } catch (_) { }
                try { document.cookie = 'came_from_trial=; Path=/; Max-Age=0'; } catch (_) { }
                logger.log('>>> REDIRECT: Dashboard with trial invite (via OAuth)');
                navigate('/dashboard');
                return;
              }
            } catch (e) {
              // swallow
            }

            // New users (haven't completed onboarding) go to Pricing first
            if (!hasCompletedOnboarding) {
              logger.log('>>> REDIRECT: Pricing page (NEW USER via OAuth)');
              // Set force_pricing flag so PricingGate can enforce it
              try { localStorage.setItem('force_pricing', 'true'); } catch (_) { }
              navigate('/pricing');
            }
            // Existing users with GMB access go to dashboard
            else if (hasGMBAccess) {
              logger.log('>>> REDIRECT: Dashboard (EXISTING USER with GMB)');
              navigate('/dashboard');
            }
            // Existing users without GMB access: set onboarding hint but send to dashboard
            else {
              logger.log('>>> REDIRECT: Dashboard (EXISTING USER without GMB) - onboarding allowed');
              try { localStorage.setItem('allow_onboarding', '1'); } catch (_) { }
              try { window.dispatchEvent(new Event('allowOnboardingSet')); } catch (_) { }
              navigate('/dashboard');
            }
          }, 1000);
        } else {
          const errorMsg = response?.data?.message || response?.message || 'Login failed';
          logger.error('Login failed:', { response, errorMsg });
          throw new Error(errorMsg);
        }
      } catch (err) {
        logger.error('OAuth callback error:', err);
        setStatus('error');
        setError(err.message || 'Authentication failed. Please try again.');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  const isSuccess = currentStep === 3;

  return (
    <PageContainer>
      <AuthCard elevation={0}>
        <LogoContainer>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#0B91D6',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '-0.5px'
            }}
          >
            Visibeen
          </Typography>
        </LogoContainer>

        <StatusIconContainer>
          {status === 'error' ? (
            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#EF4444' }} />
          ) : isSuccess ? (
            <CheckCircleIcon sx={{ fontSize: 64, color: '#10B981' }} />
          ) : (
            <CircularProgress
              size={64}
              thickness={4}
              sx={{ color: '#0B91D6' }}
            />
          )}
        </StatusIconContainer>

        <Title>
          {status === 'error'
            ? 'Authentication Failed'
            : isSuccess
              ? (isNewUser ? 'Welcome to Visibeen!' : 'Welcome Back!')
              : 'Authenticating...'}
        </Title>

        <StatusMessage sx={{ animation: status === 'error' || isSuccess ? 'none' : undefined }}>
          {status === 'error' ? 'Redirecting to login...' : status}
        </StatusMessage>

        {status === 'error' && (
          <ErrorMessage>
            <Typography sx={{ fontSize: '14px', color: '#DC2626', fontFamily: 'Inter, sans-serif' }}>
              {error}
            </Typography>
          </ErrorMessage>
        )}

        {status !== 'error' && (
          <StepIndicator>
            {steps.map((_, index) => (
              <StepDot
                key={index}
                completed={index < currentStep}
                active={index === currentStep}
              />
            ))}
          </StepIndicator>
        )}
      </AuthCard>
    </PageContainer>
  );
};

export default OAuthCallback;

